import {
  Calculator,
  Equal,
  Percent,
  Sparkles,
  Target,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EstadoError } from '@/components/ui/estado-vacio';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { usePedidoConEspera } from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import type {
  EstrategiaEnBorradorBody,
  SimulacionDeEstrategia,
  StrategyKind,
} from '../../types/strategy-applications.types';
import {
  NUMERO_DEL_METODO,
  cuerpoDeSimulacion,
  numeroValido,
  valorInicial,
  type BorradorDeEstrategia,
} from './borrador';
import { VerCualesNoSeCalculan, VerCualesYaTienenOtra, fraseDeLoQueFalta } from './ver-cuales';

/**
 * Paso 2 · Cómo calcula (maqueta `C-Paso2-Metodo`). Las cinco tarjetas y «qué
 * pasaría». «El mercado» y «el método» eran dos pantallas mostrando lo mismo:
 * acá van juntos.
 *
 * «Qué pasaría» es la estrategia EN BORRADOR simulada por el backend
 * (`POST /api/strategy-applications/simular`, 4B-datos T10) con la misma cuenta
 * que la corrida real: nada se crea. `CUSTOM_FORMULA` no se ofrece (se retira de
 * la interfaz; la base lo sigue aceptando para las que ya existen).
 */
export const METODOS: Array<{
  metodo: StrategyKind;
  titulo: string;
  ganas: string;
  perdes: string[];
}> = [
  {
    metodo: 'MATCH_LOWEST',
    titulo: 'Igualar al más barato',
    ganas: 'Quedás al nivel del más barato del mercado.',
    perdes: ['Donde ya eras más barato, sube.', 'Sin competencia leída no calcula.'],
  },
  {
    metodo: 'UNDERCUT_PERCENT',
    titulo: 'Quedar por debajo del más barato',
    ganas: 'Le ganás al más barato por el % que digas.',
    perdes: ['Cada punto que bajás sale de tu margen.', 'Sin competencia leída no calcula.'],
  },
  {
    metodo: 'MARGIN_TARGET',
    titulo: 'Apuntar a un margen',
    ganas: 'Cada producto gana lo que decidiste.',
    perdes: ['No mira el mercado: puede quedar caro.', 'Sin costo cargado no calcula.'],
  },
  {
    metodo: 'SMART_PRICING',
    titulo: 'Precio inteligente',
    ganas: 'Mira el mercado, la demanda y el stock a la vez.',
    perdes: ['Es el más difícil de explicar.', 'Con pocos datos se apoya en el mercado.'],
  },
  {
    metodo: 'DISCOUNT_PERCENT',
    titulo: 'Bajar un % de mi precio de lista',
    ganas: 'No necesita costo ni competencia: sirve desde el primer día.',
    perdes: [
      'Baja también donde ya estabas barato.',
      'Sin costo cargado nadie puede avisarte cuánto margen te queda.',
    ],
  },
];

const num = formatoCantidad;

/** El dibujo de cada método (rediseño de Compite): se reconoce sin leer. */
const ICONO_DEL_METODO: Partial<Record<StrategyKind, LucideIcon>> = {
  MATCH_LOWEST: Equal,
  UNDERCUT_PERCENT: TrendingDown,
  MARGIN_TARGET: Target,
  SMART_PRICING: Sparkles,
  DISCOUNT_PERCENT: Percent,
};

export function Paso2Metodo({
  borrador: b,
  onChange,
}: {
  borrador: BorradorDeEstrategia;
  onChange: (cambios: Partial<BorradorDeEstrategia>) => void;
}) {
  const campo = NUMERO_DEL_METODO[b.metodo];
  const cuerpo = cuerpoDeSimulacion(b);
  const pedido = usePedidoConEspera(cuerpo ? JSON.stringify(cuerpo) : null, () =>
    strategyApplicationsApi.simular(cuerpo!)
  );

  return (
    <div className="space-y-5">
      {/* Rediseño de Compite (15-oct): tarjetas con su dibujo, como los límites de
          «Nueva regla»; la elegida, en azul. */}
      <div role="radiogroup" aria-label="Cómo calcula" className="grid grid-cols-2 gap-3 text-sm">
        {METODOS.map((m, i) => {
          const elegido = b.metodo === m.metodo;
          const Icono = ICONO_DEL_METODO[m.metodo] ?? Calculator;
          return (
            <label
              key={m.metodo}
              data-testid={`metodo-${m.metodo}`}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-primary/50',
                // Lote A del 2-oct (A3): sin «Nuevo». La última, si queda sola en su
                // fila, sigue ocupando todo el ancho (como cuando era la nueva).
                i === METODOS.length - 1 && METODOS.length % 2 === 1 && 'col-span-2',
                elegido && 'border-primary bg-primary/5 ring-1 ring-primary hover:border-primary'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  elegido ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                )}
              >
                <Icono className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 space-y-1">
                <span className="flex items-center gap-2 font-semibold text-foreground">
                  {m.titulo}
                </span>
                <span className="block text-xs text-ok">Ganás: {m.ganas}</span>
                {m.perdes.map((p) => (
                  <span key={p} className="block text-xs text-muted-foreground">
                    Perdés: {p}
                  </span>
                ))}
              </span>
              <input
                type="radio"
                name="metodo"
                aria-label={m.titulo}
                checked={elegido}
                onChange={() => onChange({ metodo: m.metodo, valor: valorInicial(m.metodo) })}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
              />
            </label>
          );
        })}
      </div>

      {campo && (
        <div className="max-w-xs space-y-1">
          <Label htmlFor="numero-del-metodo">{campo.label}</Label>
          <Input
            id="numero-del-metodo"
            inputMode="decimal"
            value={b.valor}
            onChange={(e) => onChange({ valor: e.target.value })}
          />
          {numeroValido(b.metodo, b.valor) == null && (
            <p role="alert" className="text-xs text-destructive">
              Tiene que estar entre {campo.minimo} y 99 %.
            </p>
          )}
          {b.metodo === 'MARGIN_TARGET' && (
            <p className="text-xs text-muted-foreground">
              Si un producto tiene su propio margen cargado, se usa el suyo.
            </p>
          )}
          {b.metodo === 'DISCOUNT_PERCENT' && (
            <p className="text-xs text-muted-foreground">
              Se calcula siempre sobre el precio de lista, no sobre el de hoy: correrla dos veces no
              baja dos veces. Un producto sin precio de lista queda «sin datos».
            </p>
          )}
        </div>
      )}

      <section aria-label="Qué pasaría" className="space-y-2 rounded-md border p-3">
        <div className="text-sm font-medium">Qué pasaría si hoy corriera</div>
        {!cuerpo ? (
          <p className="text-sm text-muted-foreground">
            Falta completar el alcance o el número del método.
          </p>
        ) : pedido.estado === 'ERROR' ? (
          <EstadoError onReintentar={pedido.reintentar}>
            {mensajeDeError(pedido.error, 'No se pudo calcular qué pasaría.')}
          </EstadoError>
        ) : pedido.estado !== 'LISTO' ? (
          <p className="text-sm text-muted-foreground">Calculando…</p>
        ) : (
          <QuePasaria s={pedido.data} body={cuerpo} />
        )}
      </section>
    </div>
  );
}

/**
 * 27-sep (C4): el paso 2 dice sólo cuántos calcula el método y a cuántos no se
 * les puede calcular. El reparto (se aplican solos, te esperan, sin precio
 * posible, ya están bien) depende del permiso, que se elige en el paso 3, y lo
 * dice UNA vez `ReglasQueLaAlcanzan` (con «te esperan» = superan el umbral + te
 * pregunta + una regla los deja como están, re-auditoría del 26-sep). Antes el
 * paso 2 lo decía de otra manera con los mismos números: la trampa de «¿no es lo
 * mismo?». H15: no «les falta un dato», que en Hoy ya es «sin costo».
 */
export function QuePasaria({
  s,
  body,
}: {
  s: SimulacionDeEstrategia;
  /** Con el pedido, «Ver cuáles (N)» (agregados del 29-sep, A2 y A3). */
  body?: EstrategiaEnBorradorBody;
}) {
  return (
    <div data-testid="que-pasaria" className="space-y-1 text-sm">
      <p>
        De <strong>{num(s.productos)}</strong> {s.productos === 1 ? 'producto' : 'productos'}, la
        estrategia calcula <strong>{num(s.evaluados)}</strong>.
        {/* A3: lo que falta de verdad, no «competencia, costo o precio de lista» para todos. */}
        {s.sinDatos > 0 && ` ${fraseDeLoQueFalta(s.sinDatos, s.sinDatosPorMotivo)}`}
        {s.sinDatos > 0 && body && (
          <span className="ml-2 inline-block align-middle">
            <VerCualesNoSeCalculan
              body={body}
              cuantos={s.sinDatos}
              tituloDelMetodo={
                METODOS.find((m) => m.metodo === body.strategy)?.titulo ?? 'La estrategia'
              }
            />
          </span>
        )}
      </p>
      {/* Navegador T5: sin elegir, la simulación manda DEJARLOS (B2) y los que ya
          tiene otra no entran en `productos`. Sin decirlo, el paso 1 contaba 26 y
          éste 25 (o 0, con un solo producto de otra) sin explicar la diferencia. */}
      {s.yaTienenOtra > 0 && (
        <p className="text-xs text-muted-foreground">
          {s.yaTienenOtra === 1
            ? '1 ya tiene otra estrategia y no se cuenta: en el paso 3 elegís si lo toma esta.'
            : `${num(s.yaTienenOtra)} ya tienen otra estrategia y no se cuentan: en el paso 3 elegís si los toma esta.`}
          {body && (
            <span className="ml-2 inline-block align-middle">
              <VerCualesYaTienenOtra grupo={body.group} cuantos={s.yaTienenOtra} />
            </span>
          )}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Cuántos se aplicarían solos y cuántos te esperan lo ves en el paso 3, con el permiso que
        elijas; producto por producto, en el paso 4, «Resultados esperados».
      </p>
      {s.necesitanDosCorridas > 0 && (
        <p className="text-xs text-muted-foreground">
          {s.necesitanDosCorridas === 1
            ? '1 necesita más de una corrida para llegar: la regla de movimiento lo deja a mitad de camino.'
            : `${num(s.necesitanDosCorridas)} necesitan más de una corrida para llegar: la regla de movimiento los deja a mitad de camino.`}
        </p>
      )}
    </div>
  );
}
